import React from 'react';
import { Select, Spinner, SavedIcon, TextInput, Textarea } from 'evergreen-ui';
import AppButton from 'components/AppButton/AppButton';
import { TabPanel } from '../components/TabPanel';
import { SettingRow } from '../components/SettingRow';
import { SwitchRow } from '../components/SwitchRow';
import styles from '../TargetGroupSettings.module.css';

interface SettingsTabProps {
    tab: string;
    index: number;
    selectedIndex: number;
    groupType?: 'CANDIDATE' | 'INTERNAL' | 'MOTIVIEW';

    targetGroupName: string;
    setTargetGroupName: (value: string) => void;
    targetGroupDescription: string;
    setTargetGroupDescription: (value: string) => void;
    isTargetGroupPublic: boolean;
    setIsTargetGroupPublic: (value: boolean) => void;
    registrationUrl: string;
    verifyEmails: boolean;
    setVerifyEmails: (value: boolean) => void;
    autoSendReportEmails: boolean;
    setAutoSendReportEmails: (value: boolean) => void;
    isSuperAdmin: boolean;
    hideCompanyBaselineInPdf: boolean;
    setHideCompanyBaselineInPdf: (value: boolean) => void;

    isLoading: boolean;
    displaySuccess: boolean;
    onSave: () => void;
}

/**
 * Editable group settings: name, description, registration mode, email verification,
 * auto-send reports. The motiview rows that previously lived inside this
 * tab now live in MotiviewTab — saving from this tab no longer touches motiview
 * fields, so an admin can edit settings without overwriting motiview state.
 */
export const SettingsTab = ({
    tab,
    index,
    selectedIndex,
    groupType,
    targetGroupName,
    setTargetGroupName,
    targetGroupDescription,
    setTargetGroupDescription,
    isTargetGroupPublic,
    setIsTargetGroupPublic,
    registrationUrl,
    verifyEmails,
    setVerifyEmails,
    autoSendReportEmails,
    setAutoSendReportEmails,
    isSuperAdmin,
    hideCompanyBaselineInPdf,
    setHideCompanyBaselineInPdf,
    isLoading,
    displaySuccess,
    onSave,
}: SettingsTabProps) => {
    const isMotiview = groupType === 'MOTIVIEW';
    return (
    <TabPanel tab={tab} index={index} selectedIndex={selectedIndex}>
        <table>
            <colgroup>
                <col className={styles.setting_key} />
                <col className={styles.setting_value} />
            </colgroup>
            <tbody>
                <SettingRow
                    label="Name"
                    description={
                        <>
                            <p>Name of this group </p>
                            <p>
                                <span
                                    className={
                                        styles.target_group_settings__pane__configuration__bold_teal_text
                                    }>
                                    Group names are visible to candidates
                                </span>
                            </p>
                        </>
                    }>
                    <TextInput
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setTargetGroupName(e.target.value)
                        }
                        value={targetGroupName}
                        style={{ width: '100%' }}
                    />
                </SettingRow>

                <SettingRow
                    label="Description"
                    description="This is an optional description about this group, for your own reference. Candidates can not see this description.">
                    <Textarea
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setTargetGroupDescription(e.target.value)
                        }
                        value={targetGroupDescription}
                        style={{ width: '100%', resize: 'none' }}
                    />
                </SettingRow>

                {!isMotiview && (
                    <SettingRow
                        label="Registration methods"
                        description={
                            <>
                                <p>Candidate registration methods for this group</p>
                                <p>
                                    <span
                                        className={
                                            styles.target_group_settings__pane__configuration__description_title
                                        }>
                                        invites & public registration:
                                    </span>
                                    Recruiters can invite candidates using the
                                    dashboard. Anyone with the registration link can
                                    register to the group.
                                </p>
                                <p>
                                    <span
                                        className={
                                            styles.target_group_settings__pane__configuration__description_title
                                        }>
                                        invite only:
                                    </span>
                                    Only the recruiters can invite candidates using
                                    the dashboard.
                                </p>
                            </>
                        }>
                        <Select
                            width="100%"
                            value={isTargetGroupPublic ? 'invite-public' : 'invite'}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                                setIsTargetGroupPublic(
                                    e.target.value === 'invite-public',
                                )
                            }>
                            <option value="invite">invite only</option>
                            <option value="invite-public">
                                invites & public registration
                            </option>
                        </Select>
                    </SettingRow>
                )}

                {!isMotiview && isTargetGroupPublic && (
                    <SettingRow
                        label="Registration URL"
                        description={
                            'Candidates can register using this link. Requires public registration enabled via "Registration methods" settings.'
                        }>
                        <span>
                            <a
                                href={registrationUrl}
                                target="_blank"
                                className={styles.link}
                                rel="noreferrer">
                                Public registration link
                            </a>
                        </span>
                    </SettingRow>
                )}

                {!isMotiview && (
                    <SwitchRow
                        label="Email address verification"
                        description={
                            <>
                                <p>
                                    If enabled, new candidates who register via the
                                    public registration link will need to verify
                                    their email address before they can access the
                                    scan. They will receive an email containing the
                                    verification link, and once they verify their
                                    email they will receive an invitation to
                                    complete the scan.
                                </p>
                                <p>
                                    We recommend keeping this option enabled on
                                    groups with public registration enabled.
                                </p>
                            </>
                        }
                        checked={isTargetGroupPublic && verifyEmails}
                        onChange={setVerifyEmails}
                        disabled={!isTargetGroupPublic}
                    />
                )}

                {!isMotiview && (
                    <SwitchRow
                        label="Send reports automatically"
                        description="Send the report emails automatically to the candidates after analysis is finished."
                        checked={autoSendReportEmails}
                        onChange={setAutoSendReportEmails}
                    />
                )}

                {isSuperAdmin && !isMotiview && (
                    <SwitchRow
                        label="Hide company baseline on candidate PDF"
                        description="When enabled, the candidate's PDF report shows their own work preferences but no company baseline comparison or culture-match score."
                        checked={hideCompanyBaselineInPdf}
                        onChange={setHideCompanyBaselineInPdf}
                    />
                )}
            </tbody>
        </table>
        <div className={styles.submit_wrap}>
            <span className={styles.save_result}>
                {isLoading ? (
                    <Spinner size={15} />
                ) : displaySuccess ? (
                    'Saved!'
                ) : (
                    ''
                )}
            </span>
            <AppButton
                icon={SavedIcon}
                onClick={onSave}
                width={'120px'}
                isDisabled={isLoading}>
                Save
            </AppButton>
        </div>
    </TabPanel>
    );
};

export default SettingsTab;
